import { useTenantApi } from '@/account/hook/useTenantApi';
import { convertToInvoice } from '@/api/admin/payment';
import { registration } from '@/api';
import { orderIdQueryKey, orderTableQueryKey, type OrderById } from '@/api/registration';
import { type InvoicePaymentRequest } from '@/api/registration/payment';
import { type ApiResponseSingle } from '@/api/tenantClient';
import BaseFormCheckboxListField, { type CheckboxOption } from '@/base/Form/CheckboxListField';
import BaseFormInputField from '@/base/Form/InputField';
import {
  UiButton,
  UiDrawer,
  UiDrawerBody,
  UiDrawerCloseButton,
  UiDrawerContent,
  UiDrawerOverlay,
  UiHStack,
  UiStack,
  type UiDrawerProps
} from '@/lib/ui';
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useCallback, type FC } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { useEventQuery } from '@/registration/hook/useEventQuery';

dayjs.extend(timezone);

export interface OrderStatusDrawerProps {
  isOpen: UiDrawerProps['isOpen']
  onClose: UiDrawerProps['onClose']
  orderId: number
  currentStatus: string
  eventId: number
  paymentOption: string
}

export interface FormData {
  orderId: number
  actionType: string
  offlinePaymentType?: string
  offlinePaymentDate?: string
}

const getOptionsForOrder = (paymentOption: string): CheckboxOption[] => {
  const isInvoice = paymentOption === 'invoice';

  if (isInvoice) {
    return [
      { label: 'Approve', value: 'approve' },
      { label: 'Reject', value: 'reject' },
      { label: 'Resend last sent order email', value: 'resend' },
      { label: 'Send custom outstanding invoice', value: 'send_custom_outstanding_invoice' }
    ];
  } else {
    // Credit card orders
    return [
      { label: 'Convert to invoice', value: 'convert_to_invoice' },
      { label: 'Reject', value: 'reject' },
      { label: 'Resend last sent order email', value: 'resend' }
    ];
  }
};

const OrderStatusDrawer: FC<OrderStatusDrawerProps> = ({
  isOpen,
  onClose,
  orderId,
  currentStatus,
  eventId,
  paymentOption
}) => {
  const options = getOptionsForOrder(paymentOption);
  const { createTenantAdminApiRequest } = useTenantApi();
  const queryClient = useQueryClient();
  const { data: event } = useEventQuery(eventId.toString());

  const { mutateAsync: updateInvoice, isLoading: isInvoiceLoading } = useMutation<ApiResponseSingle<OrderById>, Error, InvoicePaymentRequest>({
    mutationFn: async (request) => {
      return await registration.updateInvoice(createTenantAdminApiRequest)(request);
    },
    onSuccess: (data) => {
      if (data.errors && data.errors.length > 0) {
        throw new Error(data.errors.join(', '));
      } else {
        void queryClient.invalidateQueries({ queryKey: [orderIdQueryKey, { id: orderId }] });
        void queryClient.invalidateQueries({ queryKey: [orderTableQueryKey] });
        void queryClient.invalidateQueries({ queryKey: [{ profileableType: 'Order', profileableId: orderId }] });
        onClose();
      }
    },
  });

  const { mutateAsync: convertOrder, isLoading: isConvertLoading } = useMutation<ApiResponseSingle<void>, Error, { orderId: number }>({
    mutationFn: async (request) => {
      return await convertToInvoice(createTenantAdminApiRequest)(request);
    },
    onSuccess: (data) => {
      if (data.errors && data.errors.length > 0) {
        throw new Error(data.errors.join(', '));
      } else {
        void queryClient.invalidateQueries({ queryKey: [orderIdQueryKey, { id: orderId }] });
        void queryClient.invalidateQueries({ queryKey: [orderTableQueryKey] });
        void queryClient.invalidateQueries({ queryKey: [{ profileableType: 'Order', profileableId: orderId }] });
        onClose();
      }
    },
  });

  const onSubmit = useCallback(async (
    values: FormData,
    { setSubmitting }: FormikHelpers<FormData>
  ) => {
    setSubmitting(true);

    if (values.actionType === 'convert_to_invoice') {
      await convertOrder({ orderId });
    } else {
      let formattedOfflinePaymentDate = values.offlinePaymentDate;
      if (values.offlinePaymentDate && event?.tzInfoName) {
        const dateTimeFormat = 'YYYY-MM-DD HH:mm';
        formattedOfflinePaymentDate = dayjs(values.offlinePaymentDate).tz(event.tzInfoName).format(dateTimeFormat);
      }

      await updateInvoice({
        orderId,
        actionType: values.actionType,
        offlinePaymentType: values.offlinePaymentType,
        offlinePaymentDate: formattedOfflinePaymentDate
      });
    }
    setSubmitting(false);
  }, [updateInvoice, convertOrder, orderId, event?.tzInfoName]);

  return (
    <UiDrawer
      placement={'right'}
      size={'lg'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <UiDrawerOverlay />
      <UiDrawerContent>
        <UiDrawerCloseButton size={'lg'} color={'primary.500'} />
        <UiDrawerBody p={8} py={16} bgColor={'gray.100'}>
          <Formik<FormData>
            initialValues={{
              orderId,
              actionType: currentStatus,
              offlinePaymentType: '',
              offlinePaymentDate: ''
            }}
            validateOnChange={false}
            validateOnBlur={false}
            // validationSchema={formSchema}
            onSubmit={onSubmit}
          >
            {({ values }) => {
              return (
                <Form style={{ display: 'flex', flexDirection: 'column' }}>
                  <UiStack flexGrow={1} spacing={8}>
                    <BaseFormCheckboxListField
                      label={'Order status'}
                      name={'actionType'}
                      options={options}
                      isMultiple={false}
                      layout={'stack'}
                      // helperText={'@todo This field might be hidden since admin users can choose to send attendee group specific urls to delegates.'}
                    />
                    {values.actionType === 'approve' && (
                      <>
                        <BaseFormInputField
                          name={'offlinePaymentType'}
                          label={'Offline payment type'}
                          type={'text'}
                          helperText={'If this order was paid with a method other than invoice, you can specify here. This will be reflected on the orders table.'}
                          isRequired={false}
                        />
                        <BaseFormInputField
                          name={'offlinePaymentDate'}
                          label={'Offline payment date'}
                          type={'date'}
                          helperText={'If this order was paid on a different date, please specify here. If left blank, today\'s date will be used.'}
                          isRequired={false}
                        />
                      </>
                    )}
                    <UiHStack justifyContent={'flex-end'}>
                      <UiButton px={8} size={'lg'} shadow={'base'} colorScheme={'primary'} type={'submit'} isLoading={isInvoiceLoading || isConvertLoading}>
                      Save
                      </UiButton>
                    </UiHStack>
                  </UiStack>
                </Form>
              );
            }}
          </Formik>
        </UiDrawerBody>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default OrderStatusDrawer;
